import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout";
import { login } from "../services/chatService";
import "./css/Login.css";

const Login = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  const [requireCaptcha, setRequireCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [slowLogin, setSlowLogin] = useState(false);

  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/profile");
  }, [navigate]);

  // Load Turnstile script ONCE
  useEffect(() => {
    if (window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    if (document.getElementById("cf-turnstile-script")) return;

    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);

    document.body.appendChild(script);
  }, []);

  // Render Turnstile ONLY ONCE when required
  useEffect(() => {
    if (
      scriptLoaded &&
      requireCaptcha &&
      captchaRef.current &&
      window.turnstile &&
      siteKey &&
      widgetIdRef.current === null
    ) {
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: siteKey,
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => {
          setError("CAPTCHA failed. Please refresh and try again.");
        },
      });
    }
  }, [scriptLoaded, requireCaptcha, siteKey]);

  // Cleanup Turnstile on unmount
  useEffect(() => {
    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setLoading(true);
    setSlowLogin(false);

    // ⏱ switch message after 5 seconds
    const slowTimer = setTimeout(() => {
      setSlowLogin(true);
    }, 5000);

    try {
      const res = await login(username, password, captchaToken);
      localStorage.setItem("token", res.data.token);

      // Reset captcha on success
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      setRequireCaptcha(false);
      setCaptchaToken(null);

      navigate("/profile");
    } catch (err) {
      const data = err.response?.data;

      if (data?.requireCaptcha) {
        setRequireCaptcha(true);
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current);
          setCaptchaToken(null);
        }
      }

      if (Array.isArray(data?.errors)) {
        const errorsObj = {};
        data.errors.forEach((e) => (errorsObj[e.field] = e.message));
        setFieldErrors(errorsObj);
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } finally {
      clearTimeout(slowTimer);
      setLoading(false);
      setSlowLogin(false);
    }
  };

  return (
    <AppLayout>
      <div className="login-hero">
        <div className="container">
          <h2>Welcome Back</h2>

          <p className="subtext">
            New here?{" "}
            <Link to="/register" className="register-link">
              Create an account
            </Link>
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
            {fieldErrors.username && (
              <p className="error">{fieldErrors.username}</p>
            )}

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            {fieldErrors.password && (
              <p className="error">{fieldErrors.password}</p>
            )}

            {requireCaptcha && (
              <div
                ref={captchaRef}
                className="captcha-box"
                style={{ margin: "15px 0" }}
              />
            )}

            <button
              type="submit"
              className="btn login-btn"
              disabled={loading || (requireCaptcha && !captchaToken)}
            >
              {loading ? (
                <span>
                  <i className="spinner"></i>{" "}
                  {slowLogin ? "Wait, Waking up server..." : "Logging in..."}
                </span>
              ) : (
                "Login"
              )}
            </button>
          </form>

          {error && <p className="error general-error">{error}</p>}
        </div>
      </div>
    </AppLayout>
  );
};

export default Login;
